"use client";

/**
 * /login — redesigned to match the cream/burgundy theme used across the
 * checkout, order-complete, order-pending and tour pages. The layout is
 * a two-column split on lg+ (left: visual + testimonial, right: form),
 * collapsing to a single column on mobile.
 *
 * Every existing Redux flow is preserved verbatim:
 *  - `login(email, password, handleSuccessfulLogin)` on form submit
 *  - `loginTwoFactor(token, code, handleSuccessfulLogin)` when 2FA is required
 *  - `resetLoginState()` to back out of 2FA
 *  - `addToCart(...)` + `resetBooking()` post-login when redirected from checkout
 *  - `BookingBeforeBeingLogged` rendered on the right when `?redirect=checkout`
 *  - showSignupChoices fork (particulier / professionnel / guide)
 */

import React, { FC, useState, useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { Route } from "next";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useAppSelector, useAppDispatch } from "@/app/lib/hooks";
import {
  login,
  loginTwoFactor,
  resetLoginState,
} from "@/app/lib/reducers/authReducer";
import { addToCart } from "@/app/lib/reducers/cartReducer";
import { resetBooking } from "@/app/lib/reducers/bookingReducer";
import AuthVisualPanel from "@/components/AuthVisualPanel";
import { resolvePostLoginRoute } from "@/app/lib/postLoginRoute";

// BookingBeforeBeingLogged is only rendered on the redirect=checkout flow,
// so defer its chunk to keep the initial login page small.
const BookingBeforeBeingLogged = dynamic(
  () => import("@/components/BookingBeforeBeingLogged"),
  { ssr: false, loading: () => null }
);

// Reuse the homepage's "CARNET DE VOYAGE" newsletter section under the
// login form (matches the mock 19-desktop-login). Lazy since below the fold.
//
// Rendue cote serveur (pas de `ssr: false`) : le bloc etait remplace par un
// gabarit de 220px le temps que son chunk arrive, alors qu'il en fait plus du
// double une fois monte. Tout ce qui le suit — le pied de page — sautait donc
// d'un coup a l'hydratation. Le composant n'utilise rien du navigateur au
// rendu, il n'y avait aucune raison de le sortir du HTML. Le `dynamic` reste :
// c'est lui qui garde le chunk hors du bundle initial.
const SectionNewsletter = dynamic(
  () =>
    import(
      "@/app/[lang]/[currency]/(chrome)/(home)/desktop/SectionNewsletter"
    )
);


interface UserData {
  user: {
    email: string;
    phone: string;
    uuid: string;
    role?: number;
  };
}

const PageLogin: FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [redirection, setRedirection] = useState("");
  const [showBookingReminder, setShowBookingReminder] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSignupChoices, setShowSignupChoices] = useState(false);

  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslation("guydeez");

  const booking = useAppSelector((state) => state.booking);
  const tour = useAppSelector((state) => state.tour);
  const auth = useAppSelector((state) => state.auth);

  type KnownRoute = "/account" | "/checkout" | "/signup";
  const isValidRoute = (path: string): path is KnownRoute => {
    const knownRoutes: KnownRoute[] = ["/account", "/checkout", "/signup"];
    return knownRoutes.includes(path as KnownRoute);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get("redirect");
    if (redirect === "checkout") {
      setRedirection("/signup?redirect=checkout");
      setShowBookingReminder(true);
    }
  }, []);

  // `?next=` : un chemin INTERNE au site seulement (« /fr_FR/EUR/tour/… »). Une URL
  // complète ou « //domaine » serait une redirection ouverte vers un autre site.
  // Chemin interne seulement (audit securite 2026-10-12). Le controle par prefixe se
  // contournait : « /%09/evil.com » devient « //evil.com » une fois la tabulation retiree
  // par l'URL, et menait hors du site. On resout le chemin contre l'origine courante et on
  // exige la meme origine.
  const safeNextPath = (value: string | null) => {
    if (!value || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
    if (/[\u0000-\u001f\u007f]/.test(value)) return null;
    try {
      const resolved = new URL(value, window.location.origin);
      if (resolved.origin !== window.location.origin) return null;
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    } catch {
      return null;
    }
  };

  const handleSuccessfulLogin = (userData: any) => {
    localStorage.setItem("user", JSON.stringify(userData));
    const userD = JSON.parse(localStorage.getItem("user") || "{}") as UserData;
    const urlParams = new URLSearchParams(window.location.search);
    const redirect = urlParams.get("redirect");
    const redirectPath = `/${redirect}`;

    if (redirect && isValidRoute(redirectPath)) {
      router.push("/checkout" as Route);
      dispatch(
        addToCart({
          ...booking,
          tourUuid: tour.tourDetails?.uuid || "",
          durationUuid: booking.selectedDuration?.uuid || "",
          tour: tour.tourDetails,
          user: {
            email: userD?.user?.email || "",
            phone: userD?.user?.phone || "",
            uuid: userD?.user?.uuid || "",
          },
        })
      );
      dispatch(resetBooking());
    } else if (userD && safeNextPath(urlParams.get("next"))) {
      // Connexion demandée depuis une page (ex. le coeur « favori » d'une fiche tour) :
      // retour sur cette page.
      router.push(safeNextPath(urlParams.get("next")) as Route);
    } else if (userD) {
      // Role 3 = guide → land on the guide dashboard (missions), not the
      // traveller account page. Same convention as FooterNav.
      //
      // Une agence rattachee a un reseau et SANS adresse part sur ses adresses :
      // ses prestations partent sur la facture mensuelle du siege, et une
      // facture a besoin d'une adresse. Le detail — et les replis — sont dans
      // `resolvePostLoginRoute` ; ici on attend juste sa reponse.
      //
      // `.then` plutot qu'un `await` : rendre cette fonction asynchrone en
      // ferait une promesse rendue au thunk de connexion, qui ne l'attend pas.
      resolvePostLoginRoute(userD.user).then((route) => {
        router.push(route as Route);
      });
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch(login(email, password, handleSuccessfulLogin));
  };

  const handleTwoFactorLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (auth.twoFactorToken) {
      dispatch(
        loginTwoFactor(auth.twoFactorToken, twoFactorCode, handleSuccessfulLogin)
      );
    }
  };

  const handleBackToPassword = () => {
    dispatch(resetLoginState());
    setTwoFactorCode("");
  };

  // ---------- form variants ------------------------------------------

  const renderLoginForm = () => (
    <form
      onSubmit={handleLogin}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <div>
        <div className="gdx-label" style={{ marginBottom: 6 }}>
          {t("login_email_address", { defaultValue: "Adresse email" })}
        </div>
        <input
          type="email"
          required
          autoComplete="email"
          className="gdx-input"
          placeholder="vous@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div>
        <div
          className="flex justify-between items-baseline"
          style={{ marginBottom: 6 }}
        >
          <span className="gdx-label">
            {t("login_password", { defaultValue: "Mot de passe" })}
          </span>
          <Link
            href={"/password-recovery" as Route}
            style={{
              fontSize: 12,
              color: "var(--g-coral)",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            {t("login_forgot_password", { defaultValue: "Mot de passe oublié ?" })}
          </Link>
        </div>
        <div style={{ position: "relative" }}>
          <input
            type={showPassword ? "text" : "password"}
            required
            autoComplete="current-password"
            className="gdx-input"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ paddingRight: 44 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            aria-label={
              showPassword
                ? (t("hide_password", { defaultValue: "Masquer le mot de passe" }) as string)
                : (t("show_password", { defaultValue: "Afficher le mot de passe" }) as string)
            }
            style={{
              position: "absolute",
              top: "50%",
              right: 12,
              transform: "translateY(-50%)",
              background: "transparent",
              border: 0,
              color: "var(--g-ink-500)",
              cursor: "pointer",
              padding: 4,
              display: "grid",
              placeItems: "center",
            }}
          >
            {showPassword ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Generic login error — shown when Redux flags any failure
          (wrong credentials, server error, 2FA mismatch on the first
          step, …). Intentionally vague: revealing whether the email
          exists or the password is wrong leaks signal to credential-
          stuffing scanners. */}
      {auth.error && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(224,99,90,.10)",
            border: "1px solid rgba(224,99,90,.30)",
            color: "var(--g-coral, #E0635A)",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {t("login_error_generic", {
            defaultValue:
              "Erreur de connexion. Vérifiez vos identifiants et réessayez.",
          })}
        </div>
      )}

      <button
        type="submit"
        disabled={auth.loading}
        style={{
          height: 50,
          marginTop: 6,
          width: "100%",
          fontWeight: 700,
          fontSize: 14,
          background: auth.loading
            ? "var(--g-ink-300, #cdb6a3)"
            : "var(--g-green-ada, #1F7A45)",
          color: "white",
          border: 0,
          borderRadius: 999,
          cursor: auth.loading ? "wait" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        {/* Le libelle ne change PAS pendant la connexion : le bouton desactive,
            grise et son curseur d'attente le disent deja. Il affichait jusqu'ici
            la cle `sending`, traduite « Envoi de l'email en cours » — la phrase du
            mot de passe oublie, pas celle d'une connexion. */}
        {t("login_log_in", { defaultValue: "Se connecter" })}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 12h14M13 5l7 7-7 7" />
        </svg>
      </button>
    </form>
  );

  const renderTwoFactorForm = () => (
    <form
      onSubmit={handleTwoFactorLogin}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <div
        style={{
          padding: 16,
          borderRadius: 12,
          background: "rgba(31,122,69,.08)",
          border: "1px solid rgba(31,122,69,.25)",
        }}
      >
        <h3
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: "var(--g-burgundy)",
            marginBottom: 4,
          }}
        >
          {t("two_factor_auth_required")}
        </h3>
        <p style={{ fontSize: 13, color: "var(--g-ink-700)", lineHeight: 1.4 }}>
          {t("two_factor_auth_desc", {
            email: auth.pendingUser?.email || "this account",
          })}
        </p>
      </div>

      <div>
        <div className="gdx-label" style={{ marginBottom: 6 }}>
          {t("two_factor_auth_code")}
        </div>
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoFocus
          className="gdx-input"
          placeholder="000000"
          value={twoFactorCode}
          onChange={(e) => setTwoFactorCode(e.target.value)}
          style={{
            letterSpacing: "0.4em",
            fontFamily: "ui-monospace, monospace",
            textAlign: "center",
            fontSize: 18,
            fontWeight: 700,
          }}
        />
      </div>

      {/* 2FA error — same generic banner pattern as the password
          step so the user gets feedback when the 6-digit code is
          wrong / expired. */}
      {auth.error && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(224,99,90,.10)",
            border: "1px solid rgba(224,99,90,.30)",
            color: "var(--g-coral, #E0635A)",
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          {t("login_error_generic", {
            defaultValue:
              "Erreur de connexion. Vérifiez vos identifiants et réessayez.",
          })}
        </div>
      )}

      <button
        type="submit"
        disabled={auth.loading}
        style={{
          height: 50,
          marginTop: 6,
          width: "100%",
          fontWeight: 700,
          fontSize: 14,
          background: auth.loading
            ? "var(--g-ink-300, #cdb6a3)"
            : "var(--g-green-ada, #1F7A45)",
          color: "white",
          border: 0,
          borderRadius: 999,
          cursor: auth.loading ? "wait" : "pointer",
        }}
      >
        {/* Meme raison que le bouton de connexion : pas de libelle d'attente
            emprunte a l'envoi d'e-mail. */}
        {t("two_factor_verify_code")}
      </button>

      <button
        type="button"
        onClick={handleBackToPassword}
        style={{
          fontSize: 13,
          color: "var(--g-ink-500)",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          textDecoration: "underline",
          marginTop: 4,
        }}
      >
        {t("two_factor_back_to_password")}
      </button>
    </form>
  );

  const renderSignupChoices = () => {
    const choices: {
      key: string;
      href: string;
      title: string;
      sub: string;
      iconPath: string;
    }[] = [
      {
        key: "individual",
        href: redirection
          ? "/signup?redirect=checkout"
          : "/signup",
        title: t("login_create_account_as_individual", {
          defaultValue: "Particulier",
        }) as string,
        sub: t("login_create_account_as_individual_sub", {
          defaultValue: "Je voyage",
        }) as string,
        iconPath: "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
      },
      {
        key: "business",
        href: redirection
          ? "/signup-pro?redirect=checkout"
          : "/signup-pro",
        title: t("login_create_account_as_business", {
          defaultValue: "Professionnel",
        }) as string,
        sub: t("login_create_account_as_business_sub", {
          defaultValue: "Agence, hôtel",
        }) as string,
        iconPath: "M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z",
      },
      {
        key: "guide",
        href: redirection
          ? "/recruitment?redirect=checkout"
          : "/recruitment",
        title: t("login_create_account_as_guide", {
          defaultValue: "Guide",
        }) as string,
        sub: t("login_create_account_as_guide_sub", {
          defaultValue: "Je propose",
        }) as string,
        iconPath: "M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1z",
      },
    ];

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <h1
          style={{
            fontSize: 28,
            color: "var(--g-burgundy)",
            fontWeight: 800,
            marginBottom: 4,
          }}
        >
          {t("login_create_account", { defaultValue: "Créez votre compte" })}
        </h1>
        <p
          style={{
            fontSize: 14,
            color: "var(--g-ink-500)",
            marginBottom: 12,
          }}
        >
          {t("login_which_customer_segment_do_you_belong_to")}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {choices.map((c) => (
            <Link
              key={c.key}
              href={c.href as Route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "16px 18px",
                background: "white",
                border: "1.5px solid var(--g-line)",
                borderRadius: 14,
                textDecoration: "none",
                color: "var(--g-ink-900)",
                transition: "border-color .15s ease, transform .15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--g-burgundy)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--g-line)";
              }}
            >
              <div
                className="grid place-items-center"
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 10,
                  background: "var(--g-bg-soft)",
                  color: "var(--g-burgundy)",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d={c.iconPath} />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "var(--g-burgundy)",
                  }}
                >
                  {c.title}
                </div>
                <div style={{ fontSize: 12, color: "var(--g-ink-500)" }}>
                  {c.sub}
                </div>
              </div>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--g-burgundy)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Link>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setShowSignupChoices(false)}
          style={{
            marginTop: 6,
            fontSize: 13,
            color: "var(--g-coral)",
            fontWeight: 600,
            background: "transparent",
            border: 0,
            cursor: "pointer",
            alignSelf: "center",
          }}
        >
          ← {t("login_back_to_login", { defaultValue: "Retour à la connexion" })}
        </button>
      </div>
    );
  };

  // ---------- right column (form card) ---------------------------------

  const renderFormCard = () => (
    <div
      className="gdx-card"
      style={{
        background: "white",
        padding: "32px 28px",
        borderColor: "transparent",
        boxShadow: "var(--g-shadow-md, 0 6px 24px rgba(0,0,0,.06))",
        width: "100%",
        maxWidth: 460,
        margin: "0 auto",
      }}
    >
      {showSignupChoices ? (
        renderSignupChoices()
      ) : (
        <>
          <h1
            style={{
              fontSize: "clamp(24px, 4vw, 30px)",
              color: "var(--g-burgundy)",
              fontWeight: 800,
              marginBottom: 6,
              lineHeight: 1.15,
            }}
          >
            {auth.requiresTwoFactor
              ? t("two_factor_auth_required", {
                  defaultValue: "Vérification en deux étapes",
                })
              : t("login_welcome_back", {
                  defaultValue: "Content de vous revoir.",
                })}
          </h1>
          <p
            style={{
              fontSize: 14,
              color: "var(--g-ink-500)",
              marginBottom: 22,
            }}
          >
            {auth.requiresTwoFactor
              ? t("login_two_factor_subtitle", {
                  defaultValue:
                    "Saisissez le code à 6 chiffres reçu par email.",
                })
              : t("login_subtitle", {
                  defaultValue:
                    "Connectez-vous pour gérer vos réservations.",
                })}
          </p>

          {auth.requiresTwoFactor ? renderTwoFactorForm() : renderLoginForm()}

          {!auth.requiresTwoFactor && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  margin: "22px 0 18px",
                }}
                aria-hidden="true"
              >
                <span
                  style={{
                    flex: 1,
                    height: 1,
                    background: "var(--g-line)",
                  }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: "var(--g-ink-500)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                    fontWeight: 600,
                  }}
                >
                  {t("login_or", { defaultValue: "ou" })}
                </span>
                <span
                  style={{
                    flex: 1,
                    height: 1,
                    background: "var(--g-line)",
                  }}
                />
              </div>
              <p
                style={{
                  fontSize: 13,
                  color: "var(--g-ink-500)",
                  textAlign: "center",
                }}
              >
                {t("login_new_user", {
                  defaultValue: "Pas encore de compte ?",
                })}{" "}
                <button
                  type="button"
                  onClick={() => setShowSignupChoices(true)}
                  style={{
                    color: "var(--g-coral)",
                    fontWeight: 700,
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                  }}
                >
                  {t("login_create_account", {
                    defaultValue: "Inscrivez-vous",
                  })}
                </button>
              </p>
            </>
          )}

          {/* Trust line */}
          <div
            className="flex items-center justify-center"
            style={{
              gap: 18,
              marginTop: 26,
              fontSize: 11,
              color: "var(--g-ink-500)",
              flexWrap: "wrap",
            }}
          >
            <span className="inline-flex items-center" style={{ gap: 4 }}>
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
              </svg>
              {t("login_trust_secure", {
                defaultValue: "Paiement sécurisé",
              })}
            </span>
            <span>
              {t("login_trust_rgpd", { defaultValue: "RGPD" })}
            </span>
            <span>
              {t("login_trust_support", {
                defaultValue: "Support FR 7j/7",
              })}
            </span>
          </div>
        </>
      )}
    </div>
  );

  // ---------- left column (visual) ------------------------------------


  return (
    <div
      className="nc-PageLogin"
      style={{ background: "var(--g-cream)" }}
    >
      <div
        className="grid grid-cols-1 lg:grid-cols-2"
        style={{ minHeight: "calc(100vh - 80px)" }}
      >
        <AuthVisualPanel />

        <div
          className="flex items-center justify-center"
          style={{ padding: "40px 16px" }}
        >
          {showBookingReminder ? (
            <div
              className="grid grid-cols-1 xl:grid-cols-[1fr_minmax(280px,1fr)]"
              style={{ gap: 28, width: "100%", maxWidth: 980 }}
            >
              <div>{renderFormCard()}</div>
              <div>
                <BookingBeforeBeingLogged />
              </div>
            </div>
          ) : (
            renderFormCard()
          )}
        </div>
      </div>

      <SectionNewsletter />
    </div>
  );
};

export default PageLogin;
